// /settings/window-program — 윈도우 수신기 (DeliveryOrderReceiver-v3) 다운로드 + 설치 가이드.
//
// 빌드 파일 = host volume mount (data/desktop-app/) → dvvb-api 정적 서빙 (/desktop-app/<filename>).
// 인증 X (정적 link, 페이지 자체가 login 후만 노출 = 유효 인증).
// 새 버전 release 시 host 파일 교체 + 본 파일의 RELEASE_VERSION/RELEASE_FILENAME 갱신.
//
// 섹션:
//   1. 수신기 토큰 카드 — 발급·복사·재발급
//   2. 다운로드 카드     — 버전·파일명·다운로드 버튼
//   3. 설치 가이드 카드  — 5 step
//   4. 시스템 요구사항   — Win10+, .NET 6, ESC/POS 프린터
//   5. 버전 이력         — placeholder (빈 카드)
//
// RBAC: owner+staff 모두 다운로드 가능 (모든 매장 직원이 수신기 설치 필요).
//
// ALGORITHM_FLOW 영향: 0 (UI 만, 차감 흐름 무관).

import { Badge, Card } from '../../components/primitives';
import { ReceiverTokenPanel } from './receiver-token-panel';

const RELEASE_VERSION = 'v3.3.1';
const RELEASE_FILENAME = 'DeliveryOrderReceiver-v3.3.1.exe';
const RELEASE_DOWNLOAD_URL = `https://api.dvvb.io/desktop-app/${RELEASE_FILENAME}`;

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 14,
        fontWeight: 700,
        color: 'var(--fg-1)',
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

function DownloadCard() {
  return (
    <Card style={{ padding: 20 }}>
      <SectionTitle>다운로드</SectionTitle>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 13, color: 'var(--fg-2)' }}>버전</span>
          <Badge variant="success">{RELEASE_VERSION}</Badge>
          <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>최신 버전</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--fg-2)' }}>
          파일명: <code style={{ fontSize: 12 }}>{RELEASE_FILENAME}</code>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a
            href={RELEASE_DOWNLOAD_URL}
            download={RELEASE_FILENAME}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'var(--font-sans)',
              background: 'var(--primary)',
              color: 'var(--on-primary)',
              border: '1px solid var(--primary)',
              borderRadius: 'var(--radius-md)',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'all 180ms var(--ease-out)',
            }}
          >
            다운로드 (.exe)
          </a>
        </div>
      </div>
    </Card>
  );
}

function GuideCard() {
  const steps: { n: number; text: React.ReactNode }[] = [
    { n: 1, text: <>위 버튼으로 <code>{RELEASE_FILENAME}</code> 다운로드</> },
    {
      n: 2,
      text: (
        <>
          윈도우 PC 에서 실행 (관리자 권한 필요할 수 있음)
          <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 2 }}>
            Windows 11 에서 &ldquo;스마트 앱 컨트롤이 앱을 차단했습니다&rdquo; 가 뜨면
            (실행 버튼이 없는 차단): 설정 → 개인 정보 및 보안 → Windows 보안 → 앱 및
            브라우저 컨트롤 → 스마트 앱 컨트롤 → <b>끄기</b> 후 다시 실행.
            &ldquo;추가 정보 → 실행&rdquo; 버튼이 보이는 경우는 그 버튼으로 통과.
          </div>
        </>
      ),
    },
    {
      n: 3,
      text: (
        <>
          이 페이지에서 발급한 수신기 토큰 입력
          <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 2 }}>
            토큰은 재발행 가능하며 수신기 device 인증에만 사용
          </div>
        </>
      ),
    },
    { n: 4, text: <>serverUrl 설정 = <code>https://agent.dvvb.io</code> (자동 설정)</> },
    {
      n: 5,
      text: (
        <>
          영수증 인쇄 시 자동으로 dvvb 로 push
          <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 2 }}>
            → <code>/settings/collect</code> 수신내역 탭에서 확인
          </div>
        </>
      ),
    },
  ];

  return (
    <Card style={{ padding: 20 }}>
      <SectionTitle>설치 가이드</SectionTitle>
      <ol
        style={{
          margin: 0,
          padding: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {steps.map((s) => (
          <li
            key={s.n}
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <span
              style={{
                flexShrink: 0,
                width: 22,
                height: 22,
                borderRadius: 999,
                background: 'var(--primary-bg)',
                color: 'var(--primary-strong)',
                fontSize: 12,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {s.n}
            </span>
            <div style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.6 }}>
              {s.text}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

function RequirementsCard() {
  const items = [
    'Windows 10 이상 (x64)',
    '인터넷 연결',
    '영수증 프린터 (ESC/POS) 또는 시리얼 포트',
  ];

  return (
    <Card style={{ padding: 20 }}>
      <SectionTitle>시스템 요구사항</SectionTitle>
      <ul
        style={{
          margin: 0,
          paddingLeft: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {items.map((it) => (
          <li
            key={it}
            style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.6 }}
          >
            {it}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function VersionHistoryCard() {
  const history: { version: string; date: string; note: string; latest: boolean }[] = [
    { version: 'v3.3.0', date: '2026-05-12', note: '수신기 토큰 로그인 버전', latest: true },
    { version: 'v3.1.1', date: '2026-05-05', note: '이전 로그인 방식 버전', latest: false },
    { version: 'v3.1.0', date: '2026-05-02', note: '초기 배포', latest: false },
  ];

  return (
    <Card style={{ padding: 20 }}>
      <SectionTitle>버전 이력</SectionTitle>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {history.map((h) => (
          <div
            key={h.version}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              fontSize: 13,
            }}
          >
            <Badge variant={h.latest ? 'success' : 'default'}>{h.version}</Badge>
            <span style={{ color: 'var(--fg-2)' }}>{h.date}</span>
            <span style={{ color: 'var(--fg-3)' }}>— {h.note}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function SettingsWindowProgramRoute() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--fg-1)',
            marginBottom: 4,
          }}
        >
          윈도우프로그램
        </div>
        <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>
          수신기 (DeliveryOrderReceiver-v3) 다운로드 + 설치 가이드
        </div>
      </div>
      <ReceiverTokenPanel />
      <DownloadCard />
      <GuideCard />
      <RequirementsCard />
      <VersionHistoryCard />
    </div>
  );
}
